import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BackendSquadPlayer,
  ClubCoach,
  Player,
  getUnavailabilityTypeLabel,
  getUnavailabilityReasonLabel,
} from '../../../core/models/types';
import { clubsApi } from '../../../core/http/clubs.api';
import { GameService } from '../../../core/state/game.service';
import { PlayerProfileComponent } from '../player-profile/player-profile.component';
import { IconComponent } from '../icon/icon.component';
import { ClubShieldComponent } from '../club-shield/club-shield.component';

@Component({
  selector: 'app-club-profile',
  standalone: true,
  imports: [CommonModule, PlayerProfileComponent, IconComponent, ClubShieldComponent],
  templateUrl: './club-profile.component.html',
  styleUrl: './club-profile.component.scss',
})
export class ClubProfileComponent implements AfterViewInit, OnDestroy {
  readonly game = inject(GameService);
  readonly clubId = input.required<string>();
  readonly clubName = input<string>('');
  readonly clubCode = input<string>('');
  readonly isUserClub = input<boolean>(false);
  readonly dismissible = input(true);

  readonly closed = output<void>();
  readonly customizeShield = output<void>();
  readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  readonly club = signal<any>(null);
  readonly rawPlayers = signal<BackendSquadPlayer[]>([]);
  readonly coach = signal<ClubCoach | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly retryCount = signal(0);

  readonly selectedPlayer = signal<{ id: string; name: string } | null>(null);

  openShieldEditor(): void {
    this.customizeShield.emit();
  }

  isInPortfolio(player: BackendSquadPlayer): boolean {
    const pId = player.id || player.playerDefinitionId;
    return this.game.isInWatchlist(pId);
  }

  async togglePortfolio(player: BackendSquadPlayer): Promise<void> {
    const pId = player.id || player.playerDefinitionId;
    if (this.game.isInWatchlist(pId)) {
      try {
        await this.game.removeFromPortfolio(pId);
      } catch (err: any) {
        this.game.showNotification(err?.message || 'Error al retirar de la cartera.', 'error');
      }
      return;
    }

    const currentClubId = this.clubId();
    const myClubId = this.game.userClub()?.id;

    if (this.isUserClub() || currentClubId === myClubId) {
      this.game.showNotification('No se permite añadir jugadores de tu propia plantilla a la cartera.', 'error');
      return;
    }

    if (this.game.hasMaxPortfolio()) {
      this.game.showNotification(
        'Límite alcanzado: el club solo puede tener un máximo de 3 jugadores en cartera.',
        'error'
      );
      return;
    }

    const domainPlayer: Player = {
      id: pId,
      playerInstanceId: player.id,
      playerDefinitionId: player.playerDefinitionId,
      name: player.displayName || 'Futbolista',
      shortName: player.displayName || 'Futbolista',
      position: this.positionAbbr(player.position) as any,
      age: player.age || 25,
      nationality: 'ES',
      isAmateur: (player.type || '').toUpperCase() === 'AMATEUR',
      clubId: currentClubId,
      clubName: this.displayName(),
      clubShortName: this.displayCode(),
      realClub: player.realClub,
      contract: {
        salary: player.salary ? (player.salary > 100_000 ? player.salary / 1_000_000 : player.salary) : 2.0,
        durationYears: 1,
        releaseClause: player.releaseClause ? (player.releaseClause > 100_000 ? player.releaseClause / 1_000_000 : player.releaseClause) : Math.max(10, ((player.currentMarketValue || 5000000) / 1000000) * 2.5),
        acquisitionCost: 0,
        startSeason: 1,
        endSeason: 2,
      },
      listedForTransfer: false,
      formScore: 3,
      averagePoints: player.averagePoints,
      last3AveragePoints: player.last3AveragePoints,
      rating: player.rating,
      currentMarketValue: player.currentMarketValue >= 10000 ? player.currentMarketValue / 1_000_000 : player.currentMarketValue,
    };

    try {
      await this.game.addToPortfolio(domainPlayer, myClubId);
    } catch (err: any) {
      this.game.showNotification(err?.message || 'Error al añadir el jugador a la cartera.', 'error');
    }
  }

  readonly displayName = computed(() => {
    const c = this.club();
    return c?.name || c?.club?.name || this.clubName() || 'Club';
  });

  readonly displayCode = computed(() => {
    const c = this.club();
    const code = c?.code || c?.shortName || c?.club?.code || this.clubCode();
    return code || this.displayName().slice(0, 3).toUpperCase();
  });

  readonly realPlayers = computed<BackendSquadPlayer[]>(() => {
    const list = this.rawPlayers();
    return list.filter((p) => (p.type || '').toUpperCase() === 'REAL');
  });

  readonly sortedRealPlayers = computed<BackendSquadPlayer[]>(() => {
    const posOrder: Record<string, number> = {
      POR: 1,
      DEF: 2,
      MED: 3,
      DEL: 4,
    };
    return [...this.realPlayers()].sort((a, b) => {
      const orderA = posOrder[this.positionAbbr(a.position)] ?? 99;
      const orderB = posOrder[this.positionAbbr(b.position)] ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      const valA = a.currentMarketValue ?? (a as any).marketValue ?? 0;
      const valB = b.currentMarketValue ?? (b as any).marketValue ?? 0;
      return valB - valA;
    });
  });

  readonly totalMarketValue = computed(() => {
    return this.realPlayers().reduce((acc, p) => {
      const val = p.currentMarketValue ?? (p as any).marketValue ?? 0;
      const inMillions = val >= 10000 ? val / 1_000_000 : val;
      return acc + inMillions;
    }, 0);
  });

  readonly totalGlobalAverage = computed(() =>
    this.sumPlayerPoints(player => player.averagePoints ?? player.rating));

  readonly totalLast3Average = computed(() =>
    this.sumPlayerPoints(player => player.last3AveragePoints ?? player.averagePoints ?? player.rating));

  private sumPlayerPoints(points: (player: BackendSquadPlayer) => number | null | undefined): number | null {
    const values = this.realPlayers().map(points)
      .filter((value): value is number => value != null && Number.isFinite(value));
    return values.length ? values.reduce((total, value) => total + value, 0) : null;
  }

  private previousOverflow = '';
  private dialogElement?: HTMLDialogElement;

  constructor() {
    effect((onCleanup) => {
      const id = this.clubId();
      this.retryCount();
      let active = true;
      onCleanup(() => {
        active = false;
      });

      this.loading.set(true);
      this.error.set(null);

      Promise.allSettled([clubsApi.getClub(id), clubsApi.getClubSquad(id)])
        .then(([clubRes, squadRes]) => {
          if (!active) return;

          let hasAnySuccess = false;

          if (clubRes.status === 'fulfilled' && clubRes.value) {
            this.club.set(clubRes.value);
            hasAnySuccess = true;
          }

          if (squadRes.status === 'fulfilled' && squadRes.value) {
            const squadData = squadRes.value;
            const playersList = Array.isArray(squadData)
              ? squadData
              : squadData?.players || [];
            this.rawPlayers.set(playersList);
            this.coach.set(squadData?.coach || null);
            hasAnySuccess = true;
          }

          if (!hasAnySuccess) {
            this.error.set(
              'No se ha podido cargar la información del club. Inténtalo de nuevo.'
            );
          }
        })
        .catch(() => {
          if (active) {
            this.error.set(
              'No se ha podido cargar la información del club. Inténtalo de nuevo.'
            );
          }
        })
        .finally(() => {
          if (active) this.loading.set(false);
        });
    });
  }

  ngAfterViewInit(): void {
    this.dialogElement = this.dialog().nativeElement;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialogElement.showModal();
    void this.game.fetchPortfolio(undefined, true);
  }

  ngOnDestroy(): void {
    this.dialogElement?.close();
    document.body.style.overflow = this.previousOverflow;
  }

  close(): void {
    this.dialog().nativeElement.close();
    this.closed.emit();
  }

  retry(): void {
    this.retryCount.update((c) => c + 1);
  }

  backdrop(event: MouseEvent): void {
    if (!this.dismissible()) return;
    if (event.target !== this.dialog().nativeElement) return;
    const bounds = this.dialog().nativeElement.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    ) {
      this.close();
    }
  }

  openPlayer(player: BackendSquadPlayer): void {
    const defId = player.playerDefinitionId || player.id;
    if (!defId) return;
    this.selectedPlayer.set({
      id: defId,
      name: player.displayName || (player as any).name || 'Futbolista',
    });
  }

  closePlayer(): void {
    this.selectedPlayer.set(null);
  }

  positionAbbr(value: string | undefined): string {
    if (!value) return '-';
    const map: Record<string, string> = {
      GOALKEEPER: 'POR',
      DEFENDER: 'DEF',
      MIDFIELDER: 'MED',
      FORWARD: 'DEL',
      POR: 'POR',
      DEF: 'DEF',
      MED: 'MED',
      DEL: 'DEL',
    };
    return map[value.toUpperCase()] || value;
  }

  fullPosition(value: string | undefined): string {
    const abbr = this.positionAbbr(value);
    switch (abbr) {
      case 'POR':
        return 'Portero';
      case 'DEF':
        return 'Defensa';
      case 'MED':
        return 'Centrocampista';
      case 'DEL':
        return 'Delantero';
      default:
        return value || '';
    }
  }

  positionBadgeClass(value: string | undefined): string {
    const abbr = this.positionAbbr(value);
    switch (abbr) {
      case 'DEL':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40';
      case 'MED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40';
      case 'DEF':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/40';
      case 'POR':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40';
      default:
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  }

  formatMarketValue(value: number | null | undefined): string {
    if (value == null) return 'Sin datos';
    const inMillions = value >= 10000 ? value / 1_000_000 : value;
    return `${inMillions.toFixed(1)} M€`;
  }

  getPlayerMarketValue(player: BackendSquadPlayer): string {
    const val = player.currentMarketValue ?? (player as any).marketValue;
    return this.formatMarketValue(val);
  }

  formatPoints(pts?: number | null): string {
    if (pts === null || pts === undefined || Number.isNaN(pts)) {
      return '-';
    }
    return pts.toFixed(2);
  }

  getUnavailabilityIcon(type?: string | null): string {
    const t = (type || '').toUpperCase();
    if (t === 'INJURY' || t === 'LESIÓN' || t === 'LESION') {
      return 'activity';
    }
    return 'alert-circle';
  }

  formatRating(player: BackendSquadPlayer): string {
    if (player.rating != null) {
      return new Intl.NumberFormat('es-ES', {
        maximumFractionDigits: 2,
      }).format(player.rating);
    }
    if (player.averagePoints != null) {
      return new Intl.NumberFormat('es-ES', {
        maximumFractionDigits: 2,
      }).format(player.averagePoints);
    }
    return 'Sin datos';
  }

  hasUnavailabilities(player: BackendSquadPlayer): boolean {
    return (
      Array.isArray(player.unavailabilities) &&
      player.unavailabilities.length > 0
    );
  }

  getUnavailabilityLabel(player: BackendSquadPlayer): string {
    if (!this.hasUnavailabilities(player)) return 'Disponible';
    const first = player.unavailabilities![0];
    const typeStr = getUnavailabilityTypeLabel(first.type);
    const reasonStr = getUnavailabilityReasonLabel(first.reason);
    return reasonStr ? `${typeStr}: ${reasonStr}` : typeStr;
  }

  clubShield(player: BackendSquadPlayer): string | null {
    const code = player.realClub?.shortName?.toUpperCase();
    return code ? `/crest/${code}.png` : null;
  }

  onCrestError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img.dataset['triedFallback']) {
      img.dataset['triedFallback'] = 'true';
      img.src = img.src.replace('/crest/', '/assets/crest/');
    } else {
      img.style.display = 'none';
    }
  }

  coachShield(coach: ClubCoach): string | null {
    const code = coach.realClub?.shortName?.toUpperCase();
    return code ? `/crest/${code}.png` : null;
  }

  getCoachSignedAt(coach: ClubCoach): string | null {
    return coach.signed_at || coach.signedAt || null;
  }

  getCoachInitials(name?: string): string {
    if (!name) return 'DT';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  formatModifier(val?: number | null): string {
    if (val == null || isNaN(val)) return '-';
    return Number.isInteger(val) ? val.toFixed(1) : val.toFixed(2);
  }

  onCoachImageError(event: Event): void {
    const img = event.target as HTMLElement;
    img.style.display = 'none';
  }

  getClubBadgeColor(name: string): string {
    const colors = [
      '#059669',
      '#2563eb',
      '#7c3aed',
      '#db2777',
      '#ea580c',
      '#0284c7',
      '#16a34a',
      '#d97706',
      '#dc2626',
      '#4f46e5',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }
}
